// --------------------------------------------------------------
// Created On: 2026-06-30
// Author: Dimitra Weinstein
//
// Last Modified: 2026-07-21
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useState } from "react";
import useReduxSelector from "src/hooks/useReduxSelector";
import { API, MS_PER_SECOND } from "src/constants/miscellaneous";
import { LLM_AGENT_MAX_POLLING_TIME_SECONDS } from "src/constants/polling";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import MessageList from "./MessageList/MessageList";
import AiAgentNavigationButtons from "./AiAgentNavigationButtons/AiAgentNavigationButtons";
import UserInputField from "./UserInputField/UserInputField";
import styles from "./AiAgentWindow.module.scss";

// An AI chatbot agent component that can be used to interact with users.
export default function AiAgentWindow(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string>(crypto.randomUUID());
  const [messageId, setMessageId] = useState<number>(0);
  const [pollingIntervalId, setPollingIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [userMessage, setUserMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Attempts to update messages while accounting for asynchronous state updates to prevent duplicate messages.
  function safelyAddMessage(
    setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
    sessionId: string,
    messageText: string,
    isUserMessage: boolean
  ): void {
    setMessages((prevMessages: ChatMessage[]) => {
      let lastMessage: ChatMessage | null = null;
      if (prevMessages.length > 0) {
        lastMessage = prevMessages[prevMessages.length - 1];
      }
      const lastMessageIsDifferentFromNewMessage =
        lastMessage !== null &&
        (lastMessage.isUserMessage !== isUserMessage || lastMessage.messageText !== messageText);
      if (lastMessage === null || lastMessageIsDifferentFromNewMessage) {
        return [...prevMessages, { sessionId: sessionId, messageText: messageText, isUserMessage: isUserMessage }];
      } else {
        return prevMessages;
      }
    });
  }

  // Send the user message to the AI agent.
  async function handleSendUserMessage(): Promise<void> {
    const currentSessionId = sessionId;
    let agentResponseReceived = false;
    const newMessageId = messageId + 1;
    setMessageId(newMessageId);
    safelyAddMessage(setMessages, sessionId, userMessage, true);
    const requestBody = {
      chatMessage: userMessage,
      sessionId: sessionId,
      messageId: newMessageId,
    };
    setErrorMessage("");
    setUserMessage("");
    setLoading(true);
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/llm`,
      "POST",
      requestBody
    )) as [Response, llmAgentChatResponseBody];

    if (response.ok) {
      console.log("Response received, no timeout.");
      agentResponseReceived = true;
      safelyAddMessage(setMessages, sessionId, responseBody.agentResponse, false);
      setLoading(false);
    } else if (!response.ok && response.status === 504) {
      // Begin polling the llm-message-get-lambda every second, for 15 minutes.
      const pollingTimeout = LLM_AGENT_MAX_POLLING_TIME_SECONDS * MS_PER_SECOND; // 15 minutes in milliseconds
      console.log("Begin polling `llm-message-get-lambda`. Polling will continue for up to 15 minutes.");
      const pollingInterval = setInterval(async () => {
        const [response, responseBody] = (await apiRequest(
          `${API}/company/${currentUser.companyId}/llm/session/${sessionId}/${newMessageId}`,
          "GET",
          null
        )) as [Response, polledResponseBody];

        if (response.ok && responseBody.agentResponse !== null) {
          agentResponseReceived = true;
          clearInterval(pollingInterval);
          setLoading(false);
          safelyAddMessage(setMessages, sessionId, responseBody.agentResponse, false);
        } else if (!response.ok) {
          setErrorMessage(await getApiError(response, "Unable to get response from AI agent."));
          clearInterval(pollingInterval);
        } else if (currentSessionId !== sessionId) {
          // If the sessionId has changed, stop polling for the previous session.
          clearInterval(pollingInterval);
        }
      }, 1000);

      setPollingIntervalId(pollingInterval);

      // After 15 minutes of polling, stop trying and show an error message.
      setTimeout(() => {
        console.log("Polling stopped.");
        if (agentResponseReceived || currentSessionId !== sessionId) {
          clearInterval(pollingInterval);
          setErrorMessage("");
        } else {
          clearInterval(pollingInterval);
          setErrorMessage("Unable to get response from AI agent.");
        }
        setLoading(false);
      }, pollingTimeout);
    } else {
      setErrorMessage(await getApiError(response, "Unable to get response from AI agent."));
      setLoading(false);
    }
  }

  // Handle the "Enter" key press event in the user input field.
  async function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): Promise<void> {
    if (!loading && event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      await handleSendUserMessage();
    }
  }

  // Handle ending the chat session and resetting the chat window state.
  function handleEndChatSession(): void {
    setUserMessage("");
    setSessionId(crypto.randomUUID());
    setMessageId(1);
    setErrorMessage("");
    setMessages([]);
    setLoading(false);
    if (pollingIntervalId !== null) {
      clearInterval(pollingIntervalId);
    }
  }

  return (
    <div>
      <div
        className={`${styles.overlay} ${props.showChatWindow ? styles.visible : ""}`}
        onClick={() => props.onCloseChatWindow()}
      />
      <aside
        className={`${styles.aiChatDrawer} ${props.showChatWindow ? styles.visible : ""}`}
        data-test="ai-agent-chat-window-drawer"
      >
        <div className={`flex flex-col border border-dark rounded-3 ${styles.chatWindow}`}>
          {/* AI Agent Window Title */}
          <div className="row card-title border-bottom border-dark text-center align-items-center">
            <p className=" col fs-5 text-center pt-3">
              <img src="/images/ai_microchip.png" alt="AI Agent" className={`col mr-2 mx-auto ${styles.icon}`} />
              <span className="mx-auto fs-5 p-2 py-4 pt-4">Agent</span>
            </p>
            {/* AI Agent Chat Navigation Buttons */}
            <AiAgentNavigationButtons
              onOpenNavigationDrawer={() => props.onOpenNavigationDrawer()}
              onEndChatSession={() => handleEndChatSession()}
              onCloseChatWindow={() => props.onCloseChatWindow()}
            />
          </div>
          <div className={`text-light  ${styles.chatWindowBody}`} data-test="ai-agent-chat-window-body">
            {/* AI Agent Chat Messages */}
            {messages.length > 0 && <MessageList loading={loading} errorMessage={errorMessage} messages={messages} />}
          </div>
          <div className={`card-footer border-top border-dark pt-3 ${styles.chatWindowFooter}`}>
            {/* AI Agent Chat User Input Area */}
            <UserInputField
              loading={loading}
              userMessage={userMessage}
              onHandleUserInput={(message) => setUserMessage(message)}
              onHandleKeyDown={async (e) => {
                await handleKeyDown(e);
              }}
              onHandleSendUserMessage={async () => {
                await handleSendUserMessage();
              }}
            />
            <span className="small fs-8 rounded-3 text-wrap text-light d-flex justify-content-center mt-2">
              The AI agent can make mistakes. Please double-check responses.
            </span>
          </div>
        </div>
      </aside>
    </div>
  );
}

interface Props {
  showChatWindow: boolean;
  onOpenNavigationDrawer: () => void;
  onCloseChatWindow: () => void;
}

interface ChatMessage {
  sessionId: string;
  messageText: string | null;
  isUserMessage: boolean;
}

interface llmAgentChatResponseBody {
  agentResponse: string;
}

interface polledResponseBody {
  agentResponse: string | null;
}
